

import { LinkedAccountState, ScrAuthenticationService, SignInStateEvent } from 'src/app/services/authentication.service';
import { AfterViewInit, Component } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Router } from '@angular/router';
import { formatDate } from '@angular/common';
import { BadgeService, CacheService, PendingOrderService } from 'src/app/services';

/**
 * This component supports a linked account header and dialog.
 */

@Component({
  selector: 'app-linked-accounts-header',
  templateUrl: './linked-accounts-header.component.html',
  styleUrls: ['./linked-accounts-header.component.scss']
})
export class LinkedAccountsHeaderComponent implements AfterViewInit {

  /**
   * Formatted amount of time left before being forced out.
   */
  public timeLeft?: string;

  /**
   * True if the dialog is to be visible.
   */
  public displayDialog = false;

  private sessionExpirationTimeout?: number;
  private sessionExpirationInterval?: number;

  public linkedAccountState: LinkedAccountState | undefined;
  public tokenExpirationTime?: string;
  public displayTokenExpirationTime = false;

  private static linkedSessionDialogTime = 1000 * 60 * 5; // should be 5 minutes.

  /**
   * Constructor with parameters injected.
   */
  public constructor(
    private authService: ScrAuthenticationService,
    private pendingOrderService: PendingOrderService,
    private cacheService: CacheService, 
    private badgeService: BadgeService,
    private router: Router,
    private logger: NGXLogger) {

    this.authService.getLinkedAccountState().then(state => {
      //console.debug(JSON.stringify(state));
      this.linkedAccountState = state;
    });

    this.authService.signInState.subscribe((sis: SignInStateEvent) => {
      // this.log.debug('header: user signed in ' + sis.signedIn);
      if (sis.signedIn === false) {
        this.linkedAccountState = undefined;
      }
      else {
        this.authService.getLinkedAccountState().then(state => {
          //console.debug(JSON.stringify(state));
          this.linkedAccountState = state;
        });
      }
    });
  }

  returnToManagerAccount(): void {
    this.authService.restoreLinkedAccountManagerState().then(rsp => {
      if (rsp === true) {
        this.badgeService.getBadges(true).subscribe(() => {
          // nothing to do as the data gets cached.  The UI will update.
        });

        this.pendingOrderService.clear();
        this.pendingOrderService.removeMarketBrochureStorage();
        this.cacheService.clear();
        const extras = { state: { unattended: true, saveState: false } } ;
        this.router.navigate(['dashboard', { outlets: { 'navbar-tab': ['linked-accounts', { outlets: { 'linked-accounts-tab': ['all-short-codes'] } }] } }], extras);
      }
      else if (rsp === false) {
        // for some unknown reason were unable.  A message was already displayed.
      }
    });
  }

  /**
   * Initialize the idle processing and install listeners
   * for session timeout and login/logout events.
   */
  ngAfterViewInit() {

    // Subscribe to the auth service for updates to the user.
    this.authService.idTokenExpirationTime.subscribe({
      next: (milliseconds: number) => {
        if (this.sessionExpirationTimeout) {
          window.clearTimeout(this.sessionExpirationTimeout);
          delete this.sessionExpirationTimeout;
        }
        if (this.sessionExpirationInterval) {
          window.clearInterval(this.sessionExpirationInterval);
          delete this.sessionExpirationInterval;
        }

        if (this.authService.isUserSignedIn() && milliseconds !== 0) {

          this.tokenExpirationTime = formatDate(milliseconds, 'shortTime', 'en-US');
        
          const now = new Date().getTime();
          const diff = milliseconds - now;
          // diff should be the number of milliseconds until timeout.
          this.sessionExpirationTimeout = window.setTimeout(() => {
            const nowDiff = milliseconds -  new Date().getTime();
            this.logger.debug('session timeout near..  nowdiff=' + nowDiff);
            this.setTimeLeft(nowDiff / 1000);
            this.displayDialog = true;
            if (nowDiff <= 0) {
              this.logMeOut();
              return;
            }

            this.sessionExpirationInterval = window.setInterval(() => {
              const now = new Date().getTime();
              const nowDiff = milliseconds - now;
              if (nowDiff <= 0) {
                this.logMeOut();
                return;
              }
              this.setTimeLeft(nowDiff / 1000);
            }, 1000);
          }, diff - LinkedAccountsHeaderComponent.linkedSessionDialogTime); // 5 minutes
        }
        else {
          this.timeLeft = undefined;
          this.displayDialog = false;
          this.tokenExpirationTime = undefined;
          this.displayTokenExpirationTime = false;

        }
      }
    });
  }

  /**
   * Format the number of seconds left until forced logout as mm:ss.
   * @param secs - number of seconds left before inactivity forces a logout.
   */
  setTimeLeft(secs: number) {
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs - minutes * 60);
    this.timeLeft = `${minutes}`.padStart(2, '0') + ':' + `${seconds}`.padStart(2, '0');
  }


  /**
   * User pressed the continue working button so we need to reset
   * the inactivity tracking and hide the dialog.
   */
  continueWorking() {
    this.displayDialog = false;
    this.displayTokenExpirationTime = true;
  }

  /**
   * User chose to log out (or was forced out).  Hide the dialog
   * if shown and send them to the login page similar to what
   * happens with inactivity.
   */
  logMeOut() {
    this.displayDialog = false;
    this.displayTokenExpirationTime = false;

    if (this.sessionExpirationTimeout) {
      window.clearTimeout(this.sessionExpirationTimeout);
      delete this.sessionExpirationTimeout;
    }
    if (this.sessionExpirationInterval) {
      window.clearInterval(this.sessionExpirationInterval);
      delete this.sessionExpirationInterval;
    }

    this.pendingOrderService.clear();
    this.pendingOrderService.removeMarketBrochureStorage();

    this.cacheService.clear();

    // unattended to bypass cart dialog.  display the expired token dialog on login page.
    this.router.navigate(['/sec/linkedsessiontimeout'], { state: { unattended: true, saveState: false, displayTokenExpired: true } });
  }
}
